import { useState, useMemo } from 'react';
import type { CashFlowEntry } from '../page';

interface CashFlowReportsProps {
  entries: CashFlowEntry[];
  clients: { id: string; name: string }[];
  projects: { id: string; name: string }[];
}

type ReportType = 'period' | 'by_client' | 'by_project' | 'debits';

export default function CashFlowReports({ entries, clients, projects }: CashFlowReportsProps) {
  const [reportType, setReportType] = useState<ReportType>('period');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const filteredByPeriod = useMemo(() =>
    entries.filter(e => e.reference_date >= dateFrom && e.reference_date <= dateTo),
    [entries, dateFrom, dateTo]
  );

  // Relatório por período (agrupado por mês)
  const periodReport = useMemo(() => {
    const months: Record<string, { credits: number; debits: number; count: number }> = {};
    filteredByPeriod.forEach(e => {
      const month = e.reference_date.substring(0, 7);
      if (!months[month]) months[month] = { credits: 0, debits: 0, count: 0 };
      if (e.flow_type === 'credit') months[month].credits += Number(e.amount);
      else months[month].debits += Number(e.amount);
      months[month].count++;
    });
    return Object.entries(months)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, data]) => ({
        month,
        label: new Date(month + '-15').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
        ...data,
        balance: data.credits - data.debits,
      }));
  }, [filteredByPeriod]);

  // Créditos por cliente
  const byClientReport = useMemo(() => {
    const map: Record<string, { name: string; credits: number; debits: number; count: number }> = {};
    filteredByPeriod.forEach(e => {
      const key = e.client_id || '__none__';
      const name = e._client_name || 'Sem cliente';
      if (!map[key]) map[key] = { name, credits: 0, debits: 0, count: 0 };
      if (e.flow_type === 'credit') map[key].credits += Number(e.amount);
      else map[key].debits += Number(e.amount);
      map[key].count++;
    });
    return Object.values(map).sort((a, b) => b.credits - a.credits);
  }, [filteredByPeriod]);

  // Créditos por projeto
  const byProjectReport = useMemo(() => {
    const map: Record<string, { name: string; credits: number; debits: number; count: number }> = {};
    filteredByPeriod.forEach(e => {
      const key = e.project_id || '__none__';
      const name = e._project_name || 'Sem projeto';
      if (!map[key]) map[key] = { name, credits: 0, debits: 0, count: 0 };
      if (e.flow_type === 'credit') map[key].credits += Number(e.amount);
      else map[key].debits += Number(e.amount);
      map[key].count++;
    });
    return Object.values(map).sort((a, b) => b.credits - a.credits);
  }, [filteredByPeriod]);

  // Débitos por período
  const debitEntries = useMemo(() =>
    filteredByPeriod.filter(e => e.flow_type === 'debit').sort((a, b) =>
      b.reference_date.localeCompare(a.reference_date)
    ),
    [filteredByPeriod]
  );

  const totalCredits = filteredByPeriod.filter(e => e.flow_type === 'credit').reduce((s, e) => s + Number(e.amount), 0);
  const totalDebits = filteredByPeriod.filter(e => e.flow_type === 'debit').reduce((s, e) => s + Number(e.amount), 0);
  const balance = totalCredits - totalDebits;

  const fmtDate = (d: string) => new Date(d + 'T12:00:00').toLocaleDateString('pt-BR');

  const ORIGIN_LABELS: Record<string, string> = {
    manual: 'Manual', project: 'Projeto', task: 'Atividade',
    advance: 'Adiantamento', refund: 'Reembolso', expense: 'Despesa',
    salary: 'Salário', tax: 'Imposto', tool: 'Ferramenta', other: 'Outro', auto: 'Automático',
  };

  return (
    <div>
      {/* Seletor de relatório + período */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
          {([
            { key: 'period', label: 'Por Período' },
            { key: 'by_client', label: 'Por Cliente' },
            { key: 'by_project', label: 'Por Projeto' },
            { key: 'debits', label: 'Débitos' },
          ] as { key: ReportType; label: string }[]).map(r => (
            <button
              key={r.key}
              onClick={() => setReportType(r.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                reportType === r.key
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-slate-500">Período:</span>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300"
          />
          <span className="text-slate-400 text-xs">até</span>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-300"
          />
        </div>
      </div>

      {/* Cards de resumo do período */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-slate-400 text-xs font-medium uppercase tracking-wide">Créditos no período</p>
          <p className="text-xl font-bold text-emerald-600 mt-1">{fmt(totalCredits)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-slate-400 text-xs font-medium uppercase tracking-wide">Débitos no período</p>
          <p className="text-xl font-bold text-red-500 mt-1">{fmt(totalDebits)}</p>
        </div>
        <div className={`rounded-xl border p-4 ${balance >= 0 ? 'bg-white border-slate-200' : 'bg-red-50 border-red-200'}`}>
          <p className="text-slate-400 text-xs font-medium uppercase tracking-wide">Saldo do período</p>
          <p className={`text-xl font-bold mt-1 ${balance >= 0 ? 'text-slate-800' : 'text-red-600'}`}>{fmt(balance)}</p>
        </div>
      </div>

      {/* Relatório: Por Período */}
      {reportType === 'period' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="text-slate-700 font-semibold text-sm">Saldo por Mês</h3>
          </div>
          {periodReport.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm">Nenhum lançamento no período</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Mês</th>
                  <th className="text-right px-5 py-3 text-slate-500 font-medium">Créditos</th>
                  <th className="text-right px-5 py-3 text-slate-500 font-medium">Débitos</th>
                  <th className="text-right px-5 py-3 text-slate-500 font-medium">Saldo</th>
                  <th className="text-right px-5 py-3 text-slate-500 font-medium">Lançamentos</th>
                </tr>
              </thead>
              <tbody>
                {periodReport.map(row => (
                  <tr key={row.month} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-5 py-3 text-slate-700 font-medium capitalize">{row.label}</td>
                    <td className="px-5 py-3 text-right text-emerald-600 font-medium">{fmt(row.credits)}</td>
                    <td className="px-5 py-3 text-right text-red-500 font-medium">{fmt(row.debits)}</td>
                    <td className={`px-5 py-3 text-right font-bold ${row.balance >= 0 ? 'text-slate-800' : 'text-red-600'}`}>
                      {fmt(row.balance)}
                    </td>
                    <td className="px-5 py-3 text-right text-slate-400 text-xs">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Relatório: Por Cliente */}
      {reportType === 'by_client' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="text-slate-700 font-semibold text-sm">Créditos por Cliente</h3>
          </div>
          {byClientReport.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm">Nenhum lançamento no período</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Cliente</th>
                  <th className="text-right px-5 py-3 text-slate-500 font-medium">Créditos</th>
                  <th className="text-right px-5 py-3 text-slate-500 font-medium">Débitos</th>
                  <th className="text-right px-5 py-3 text-slate-500 font-medium">Saldo</th>
                  <th className="text-right px-5 py-3 text-slate-500 font-medium">Lançamentos</th>
                </tr>
              </thead>
              <tbody>
                {byClientReport.map((row, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 shrink-0">
                          <i className="ri-building-line text-slate-500 text-xs"></i>
                        </div>
                        <span className="text-slate-700 font-medium">{row.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right text-emerald-600 font-medium">{fmt(row.credits)}</td>
                    <td className="px-5 py-3 text-right text-red-500 font-medium">{fmt(row.debits)}</td>
                    <td className={`px-5 py-3 text-right font-bold ${(row.credits - row.debits) >= 0 ? 'text-slate-800' : 'text-red-600'}`}>
                      {fmt(row.credits - row.debits)}
                    </td>
                    <td className="px-5 py-3 text-right text-slate-400 text-xs">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Relatório: Por Projeto */}
      {reportType === 'by_project' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="text-slate-700 font-semibold text-sm">Créditos por Projeto</h3>
          </div>
          {byProjectReport.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm">Nenhum lançamento no período</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Projeto</th>
                  <th className="text-right px-5 py-3 text-slate-500 font-medium">Créditos</th>
                  <th className="text-right px-5 py-3 text-slate-500 font-medium">Débitos</th>
                  <th className="text-right px-5 py-3 text-slate-500 font-medium">Saldo</th>
                  <th className="text-right px-5 py-3 text-slate-500 font-medium">Lançamentos</th>
                </tr>
              </thead>
              <tbody>
                {byProjectReport.map((row, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 shrink-0">
                          <i className="ri-folder-line text-slate-500 text-xs"></i>
                        </div>
                        <span className="text-slate-700 font-medium">{row.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right text-emerald-600 font-medium">{fmt(row.credits)}</td>
                    <td className="px-5 py-3 text-right text-red-500 font-medium">{fmt(row.debits)}</td>
                    <td className={`px-5 py-3 text-right font-bold ${(row.credits - row.debits) >= 0 ? 'text-slate-800' : 'text-red-600'}`}>
                      {fmt(row.credits - row.debits)}
                    </td>
                    <td className="px-5 py-3 text-right text-slate-400 text-xs">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Relatório: Débitos */}
      {reportType === 'debits' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-slate-700 font-semibold text-sm">Débitos no Período</h3>
            <span className="text-red-500 font-bold text-sm">{fmt(totalDebits)}</span>
          </div>
          {debitEntries.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm">Nenhum débito no período</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Data</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Descrição</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Origem</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Cliente</th>
                  <th className="text-left px-5 py-3 text-slate-500 font-medium">Projeto</th>
                  <th className="text-right px-5 py-3 text-slate-500 font-medium">Valor</th>
                </tr>
              </thead>
              <tbody>
                {debitEntries.map(e => (
                  <tr key={e.id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">
                      {fmtDate(e.reference_date)}
                    </td>
                    <td className="px-5 py-3 text-slate-700 max-w-[200px] truncate">{e.description || '—'}</td>
                    <td className="px-5 py-3">
                      <span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">
                        {ORIGIN_LABELS[e.origin] || e.origin}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-slate-500 text-xs">{e._client_name}</td>
                    <td className="px-5 py-3 text-slate-500 text-xs">{e._project_name}</td>
                    <td className="px-5 py-3 text-right text-red-500 font-semibold">
                      -{Number(e.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}